import { c as _c } from "react/compiler-runtime";
import type { ReactNode } from 'react';
import type { Address } from 'viem';
import { MathLib } from '@morpho-org/blue-sdk';
import { ZERO } from '@morpho-org/dapp-core';
import { isDefined } from '@morpho-org/morpho-ts';
import { CardText, Skeleton } from '@morpho-org/ui-v6/server';
import { Icon, Separator, Tooltip, TooltipContent, TooltipTrigger } from '@repo/ui';
import { ArrowRight } from '@repo/ui/icons';
import { abbreviateAddress } from '@repo/utils';
import type { ForceDeallocationContext } from '@/components/common/VaultV2FeeRow';
import { SimulationEstimateBadge } from '@/components/common/SimulationEstimateBadge';
import { ForceDeallocationFeeExplanation } from '@/components/common/VaultV2FeeRow';
import { formatTokenAmount } from '@/helpers/formatters';
import type { VaultV2RecapSource } from './hooks/useVaultV2RecapData';
import { useVaultV2RecapFormatting } from './hooks/useVaultV2RecapFormatting';
import RecapApyRow from './RecapApyRow';
import { RecapContainer } from './RecapContainer';
import RecapInteraction from './RecapInteraction';
import RecapLabeledRow from './RecapLabeledRow';
import { RecapApyRowSkeleton, RecapInteractionSkeleton, RecapPositionRowSkeleton, RecapTextSkeleton, RecapVaultHeaderSkeleton } from './RecapSkeletons';
import RecapVaultHeader from './RecapVaultHeader';
export interface VaultV2RecapProps {
  type: 'vaultV2Deposit' | 'vaultV2Withdraw';
  amount: bigint;
  initialPosition: bigint;
  finalPosition: bigint | undefined;
  vaultAddress: Address;
  feeAssets?: bigint;
  /**
   * What the transaction deliberately leaves deposited and never pays out — the penalty-drift
   * headroom a buffered MAX `forceRedeem` reserves. Zero on every other path, and on deposits.
   */
  retainedAssets?: bigint;
  /** What the fee is charged on, when the caller can state the whole split. */
  forceDeallocation?: ForceDeallocationContext;
  /** Shows a skeleton in place of the position-after value while the EVM simulation resolves. */
  isFinalPositionLoading?: boolean;
  /** Position-after is an estimate: a simulation ran but couldn't verify the outcome. */
  isSimulationFallback?: boolean;
  /** The procedure the recap reads, matching the dialog it was opened from. */
  source?: VaultV2RecapSource;
}
const VAULT_OPERATION_LABEL = {
  vaultV2Deposit: 'Deposit',
  vaultV2Withdraw: 'Withdraw'
};
const VaultV2Recap = t0 => {
  const $ = _c(91);
  const {
    type,
    amount,
    initialPosition,
    finalPosition,
    vaultAddress,
    feeAssets,
    retainedAssets,
    forceDeallocation,
    isFinalPositionLoading,
    isSimulationFallback,
    source
  } = t0;
  let t1;
  if ($[0] !== finalPosition || $[1] !== initialPosition || $[2] !== source || $[3] !== vaultAddress) {
    t1 = {
      vaultAddress,
      initialPosition,
      finalPosition,
      source
    };
    $[0] = finalPosition;
    $[1] = initialPosition;
    $[2] = source;
    $[3] = vaultAddress;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const formatting = useVaultV2RecapFormatting(t1);
  const {
    vaultV2,
    vaultAsset,
    formattedInitialPosition,
    detailedFormattedInitialPosition,
    formattedFinalPosition,
    detailedFormattedFinalPosition,
    loading
  } = formatting;
  const hasFee = feeAssets !== undefined && feeAssets > ZERO;
  if (loading) {
    let t2;
    if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <RecapVaultHeaderSkeleton />;
      $[5] = t2;
    } else {
      t2 = $[5];
    }
    const t3 = VAULT_OPERATION_LABEL[type];
    let t4;
    if ($[6] !== t3) {
      t4 = <RecapInteractionSkeleton label={t3} />;
      $[6] = t3;
      $[7] = t4;
    } else {
      t4 = $[7];
    }
    let t5;
    if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = <Separator />;
      $[8] = t5;
    } else {
      t5 = $[8];
    }
    let t6;
    if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = <div className="gap-sm flex flex-col items-stretch"><RecapPositionRowSkeleton /><RecapApyRowSkeleton /></div>;
      $[9] = t6;
    } else {
      t6 = $[9];
    }
    let t7;
    if ($[10] !== t4) {
      t7 = <RecapContainer className="gap-lg flex-col">{t2}<div className="gap-sm flex flex-col items-stretch">{t4}{t5}{t6}</div></RecapContainer>;
      $[10] = t4;
      $[11] = t7;
    } else {
      t7 = $[11];
    }
    let t8;
    if ($[12] !== hasFee) {
      t8 = hasFee && <RecapLabeledRow label={RECEIVE_ROW_LABEL} value={<RecapTextSkeleton />} />;
      $[12] = hasFee;
      $[13] = t8;
    } else {
      t8 = $[13];
    }
    let t9;
    if ($[14] !== t7 || $[15] !== t8) {
      t9 = withReceiveCard(t7, t8);
      $[14] = t7;
      $[15] = t8;
      $[16] = t9;
    } else {
      t9 = $[16];
    }
    return t9;
  }
  if (!isDefined(vaultV2) || !isDefined(vaultAsset)) {
    let t2;
    if ($[17] !== vaultAddress) {
      t2 = abbreviateAddress(vaultAddress);
      $[17] = vaultAddress;
      $[18] = t2;
    } else {
      t2 = $[18];
    }
    const t3 = `Cannot display interaction with vault ${t2}`;
    let t4;
    if ($[19] !== t3) {
      t4 = <CardText height="240px" text={t3} />;
      $[19] = t3;
      $[20] = t4;
    } else {
      t4 = $[20];
    }
    return t4;
  }
  let t2;
  if ($[21] !== vaultV2.image || $[22] !== vaultV2.name) {
    t2 = <RecapVaultHeader image={vaultV2.image} name={vaultV2.name} />;
    $[21] = vaultV2.image;
    $[22] = vaultV2.name;
    $[23] = t2;
  } else {
    t2 = $[23];
  }
  const t3 = VAULT_OPERATION_LABEL[type];
  let t4;
  if ($[24] !== amount || $[25] !== t3 || $[26] !== vaultAsset) {
    t4 = <RecapInteraction amount={amount} asset={vaultAsset} label={t3} />;
    $[24] = amount;
    $[25] = t3;
    $[26] = vaultAsset;
    $[27] = t4;
  } else {
    t4 = $[27];
  }
  let t5;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <Separator />;
    $[28] = t5;
  } else {
    t5 = $[28];
  }
  let t6;
  if ($[29] !== vaultAsset.symbol) {
    t6 = <span className="text-secondary-foreground text-xs">Deposit ({vaultAsset.symbol})</span>;
    $[29] = vaultAsset.symbol;
    $[30] = t6;
  } else {
    t6 = $[30];
  }
  let t7;
  if ($[31] !== formattedInitialPosition) {
    t7 = <TooltipTrigger asChild={true}><span className="text-muted-foreground text-xs">{formattedInitialPosition}</span></TooltipTrigger>;
    $[31] = formattedInitialPosition;
    $[32] = t7;
  } else {
    t7 = $[32];
  }
  let t8;
  if ($[33] !== detailedFormattedInitialPosition) {
    t8 = !!detailedFormattedInitialPosition && <TooltipContent>{detailedFormattedInitialPosition}</TooltipContent>;
    $[33] = detailedFormattedInitialPosition;
    $[34] = t8;
  } else {
    t8 = $[34];
  }
  let t9;
  if ($[35] !== t7 || $[36] !== t8) {
    t9 = <Tooltip>{t7}{t8}</Tooltip>;
    $[35] = t7;
    $[36] = t8;
    $[37] = t9;
  } else {
    t9 = $[37];
  }
  let t10;
  if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <Icon className="text-foreground" icon={<ArrowRight />} />;
    $[38] = t10;
  } else {
    t10 = $[38];
  }
  let t11;
  if ($[39] !== detailedFormattedFinalPosition || $[40] !== formattedFinalPosition || $[41] !== isFinalPositionLoading) {
    t11 = isFinalPositionLoading ? <Skeleton className="h-(--text-xs--line-height) w-[56px]" /> : <Tooltip><TooltipTrigger asChild={true}><span className="text-foreground text-xs">{formattedFinalPosition}</span></TooltipTrigger>{!!detailedFormattedFinalPosition && <TooltipContent>{detailedFormattedFinalPosition}</TooltipContent>}</Tooltip>;
    $[39] = detailedFormattedFinalPosition;
    $[40] = formattedFinalPosition;
    $[41] = isFinalPositionLoading;
    $[42] = t11;
  } else {
    t11 = $[42];
  }
  let t12;
  if ($[43] !== finalPosition || $[44] !== isFinalPositionLoading || $[45] !== isSimulationFallback) {
    t12 = isSimulationFallback && !isFinalPositionLoading && isDefined(finalPosition) && <SimulationEstimateBadge />;
    $[43] = finalPosition;
    $[44] = isFinalPositionLoading;
    $[45] = isSimulationFallback;
    $[46] = t12;
  } else {
    t12 = $[46];
  }
  let t13;
  if ($[47] !== t11 || $[48] !== t12 || $[49] !== t9) {
    t13 = <div className="flex items-center gap-[2px]">{t9}{t10}{t11}{t12}</div>;
    $[47] = t11;
    $[48] = t12;
    $[49] = t9;
    $[50] = t13;
  } else {
    t13 = $[50];
  }
  let t14;
  if ($[51] !== t13 || $[52] !== t6) {
    t14 = <div className="flex items-center justify-between gap-[16px]">{t6}{t13}</div>;
    $[51] = t13;
    $[52] = t6;
    $[53] = t14;
  } else {
    t14 = $[53];
  }
  let t15;
  if ($[54] !== formatting || $[55] !== vaultAsset) {
    t15 = <RecapApyRow asset={vaultAsset} formatting={formatting} />;
    $[54] = formatting;
    $[55] = vaultAsset;
    $[56] = t15;
  } else {
    t15 = $[56];
  }
  let t16;
  if ($[57] !== t14 || $[58] !== t15) {
    t16 = <div className="gap-sm flex flex-col items-stretch">{t14}{t15}</div>;
    $[57] = t14;
    $[58] = t15;
    $[59] = t16;
  } else {
    t16 = $[59];
  }
  let t17;
  if ($[60] !== t16 || $[61] !== t4) {
    t17 = <div className="gap-sm flex flex-col items-stretch">{t4}{t5}{t16}</div>;
    $[60] = t16;
    $[61] = t4;
    $[62] = t17;
  } else {
    t17 = $[62];
  }
  let t18;
  if ($[63] !== t17 || $[64] !== t2) {
    t18 = <RecapContainer className="gap-lg flex-col">{t2}{t17}</RecapContainer>;
    $[63] = t17;
    $[64] = t2;
    $[65] = t18;
  } else {
    t18 = $[65];
  }
  const card = t18;
  if (!hasFee) {
    return card;
  }
  let T0;
  let t19;
  let t20;
  let t21;
  let t22;
  let t23;
  if ($[66] !== amount || $[67] !== card || $[68] !== feeAssets || $[69] !== forceDeallocation || $[70] !== retainedAssets || $[71] !== vaultAsset) {
    const walletAssets = MathLib.zeroFloorSub(amount, feeAssets + (retainedAssets ?? ZERO));
    t22 = withReceiveCard;
    t23 = card;
    T0 = RecapLabeledRow;
    t19 = RECEIVE_ROW_LABEL;
    if ($[78] !== feeAssets || $[79] !== forceDeallocation || $[80] !== vaultAsset) {
      t20 = <ForceDeallocationFeeExplanation vaultAsset={vaultAsset} feeAssets={feeAssets} forceDeallocation={forceDeallocation} />;
      $[78] = feeAssets;
      $[79] = forceDeallocation;
      $[80] = vaultAsset;
      $[81] = t20;
    } else {
      t20 = $[81];
    }
    t21 = formatTokenAmount(vaultAsset, walletAssets);
    $[66] = amount;
    $[67] = card;
    $[68] = feeAssets;
    $[69] = forceDeallocation;
    $[70] = retainedAssets;
    $[71] = vaultAsset;
    $[72] = T0;
    $[73] = t19;
    $[74] = t20;
    $[75] = t21;
    $[76] = t22;
    $[77] = t23;
  } else {
    T0 = $[72];
    t19 = $[73];
    t20 = $[74];
    t21 = $[75];
    t22 = $[76];
    t23 = $[77];
  }
  let t24;
  if ($[82] !== T0 || $[83] !== t19 || $[84] !== t20 || $[85] !== t21) {
    t24 = <T0 label={t19} tooltip={t20} value={t21} />;
    $[82] = T0;
    $[83] = t19;
    $[84] = t20;
    $[85] = t21;
    $[86] = t24;
  } else {
    t24 = $[86];
  }
  let t25;
  if ($[87] !== t22 || $[88] !== t23 || $[89] !== t24) {
    t25 = t22(t23, t24);
    $[87] = t22;
    $[88] = t23;
    $[89] = t24;
    $[90] = t25;
  } else {
    t25 = $[90];
  }
  return t25;
};
const RECEIVE_ROW_LABEL = 'To your wallet (net of fees)';

/** Stacks the "You receive" card under the recap when the withdrawal pays a fee. */
const withReceiveCard = (card: ReactNode, receiveRow: ReactNode) => receiveRow ? <div className="gap-xs flex flex-col">
      {card}
      <RecapContainer className="gap-sm flex-col">
        <span className="text-secondary-foreground text-xs">You receive</span>
        {receiveRow}
      </RecapContainer>
    </div> : card;
export default VaultV2Recap;